import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import {
  DollarSign, TrendingUp, TrendingDown, Wallet, Pencil, Check, X, BarChart3,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell,
} from "recharts";

const COLORS = {
  bull: "#00ff9c",
  bear: "#ff4757",
  axis: "rgba(255,255,255,0.35)",
  grid: "rgba(255,255,255,0.06)",
};

const EXPENSE_LABELS = {
  emergent: "Emergent",
  domain: "Domain",
  data_api: "Data / API",
  stripe_fees: "Stripe / payment",
  marketing: "Marketing / ads",
  other: "Other",
};

function money(n) {
  const num = Number(n || 0);
  const abs = Math.abs(num);
  return `${num < 0 ? "-" : ""}$${abs.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function RevenueStat({ icon: Icon, label, value, sub, accent = "var(--bull)", testid }) {
  return (
    <div className="card-surface p-4 relative overflow-hidden" data-testid={testid}>
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{ background: `radial-gradient(circle at 100% 0%, ${accent}22, transparent 60%)` }}
      />
      <div className="relative">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
          <div className="label-mono">{label}</div>
        </div>
        <div
          className="font-mono text-2xl text-white font-semibold tabular-nums mt-1.5 tracking-tight"
          style={{ textShadow: `0 0 14px ${accent}44` }}
        >
          {value}
        </div>
        {sub && <div className="text-[11px] text-white/45 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function ExpenseEditor({ expenses, onSave }) {
  const [draft, setDraft] = useState(expenses || {});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!editing) setDraft(expenses || {}); }, [expenses, editing]);

  const update = (k, v) => {
    const n = v === "" ? "" : Number(v);
    setDraft((d) => ({ ...d, [k]: Number.isFinite(n) ? n : v }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of Object.keys(EXPENSE_LABELS)) {
        const v = draft[k];
        payload[k] = v === "" || v == null ? 0 : Math.max(0, Number(v));
      }
      await onSave(payload);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setDraft(expenses || {}); setEditing(false); };

  const total = Object.keys(EXPENSE_LABELS).reduce(
    (a, k) => a + Number((editing ? draft[k] : expenses?.[k]) || 0),
    0
  );

  return (
    <div className="card-surface p-4 sm:p-5" data-testid="expense-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Wallet className="w-3.5 h-3.5 text-white/70" />
          <div className="text-white text-[14px] font-semibold tracking-tight">Monthly expenses</div>
        </div>
        {editing ? (
          <div className="flex items-center gap-1">
            <button
              onClick={save}
              disabled={saving}
              className="btn-accent text-[11px] py-1.5 px-3 inline-flex items-center gap-1.5"
              data-testid="expense-save-btn"
            >
              <Check className="w-3 h-3" /> Save
            </button>
            <button
              onClick={cancel}
              className="btn-ghost text-[11px] py-1.5 px-3 inline-flex items-center gap-1.5"
              data-testid="expense-cancel-btn"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="btn-ghost text-[11px] py-1.5 px-3 inline-flex items-center gap-1.5"
            data-testid="expense-edit-btn"
          >
            <Pencil className="w-3 h-3" /> Edit
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {Object.entries(EXPENSE_LABELS).map(([k, label]) => (
          <div key={k} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.05]">
            <label className="text-[12px] text-white/65" htmlFor={`expense-${k}`}>{label}</label>
            <div className="flex items-center gap-1">
              <span className="text-white/40 text-[12px]">$</span>
              {editing ? (
                <input
                  id={`expense-${k}`}
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  value={draft[k] ?? ""}
                  onChange={(e) => update(k, e.target.value)}
                  placeholder="0"
                  className="w-24 bg-transparent border-b border-white/15 focus:border-[var(--bull)] outline-none text-right font-mono text-[13px] text-white tabular-nums py-0.5"
                  data-testid={`expense-input-${k}`}
                />
              ) : (
                <span
                  className="font-mono text-white text-[13px] tabular-nums min-w-[3rem] text-right"
                  data-testid={`expense-value-${k}`}
                >
                  {Number(expenses?.[k] || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <span className="label-mono">Total / month</span>
        <span className="font-mono text-white text-[15px] font-semibold tabular-nums" data-testid="expense-total">
          {money(total)}
        </span>
      </div>
    </div>
  );
}

function ProfitChart({ data }) {
  // Show last 12 months. Bars colored bull/bear based on profit sign.
  const formatted = useMemo(
    () => (data || []).map((r) => ({
      ...r,
      monthShort: r.month.slice(2).replace("-", "/"),
    })),
    [data]
  );
  return (
    <div className="card-surface p-4 sm:p-5" data-testid="profit-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[var(--bull)]" />
          <div className="text-white text-[15px] tracking-tight font-semibold">Profit by month</div>
        </div>
        <div className="text-[11px] text-white/45 font-mono">last {formatted.length} months</div>
      </div>
      <div className="h-[260px]">
        <ResponsiveContainer>
          <BarChart data={formatted} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid stroke={COLORS.grid} vertical={false} />
            <XAxis dataKey="monthShort" tick={{ fill: COLORS.axis, fontSize: 10 }} stroke={COLORS.grid} />
            <YAxis tick={{ fill: COLORS.axis, fontSize: 10 }} stroke={COLORS.grid} tickFormatter={(v) => `$${v}`} />
            <Tooltip
              contentStyle={{
                background: "rgba(10,10,12,0.95)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 10,
                fontSize: 12,
              }}
              labelStyle={{ color: "#fff" }}
              formatter={(v, name) => [money(v), name]}
            />
            <ReferenceLine y={0} stroke={COLORS.grid} />
            <Bar dataKey="profit" radius={[4, 4, 0, 0]}>
              {formatted.map((entry, i) => (
                <Cell key={i} fill={entry.profit >= 0 ? COLORS.bull : COLORS.bear} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function RevenuePanel() {
  const [rev, setRev] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedToast, setSavedToast] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, h] = await Promise.all([
        api.get("/api/admin/analytics/revenue"),
        api.get("/api/admin/analytics/revenue/history?months=12"),
      ]);
      setRev(r.data);
      setHistory(h.data.series || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveExpenses = async (payload) => {
    await api.put("/api/admin/analytics/expenses", payload);
    setSavedToast(true);
    setTimeout(() => setSavedToast(false), 1800);
    await load();
  };

  if (!rev) {
    return (
      <div className="card-surface p-6" data-testid="revenue-panel-loading">
        <div className="label-mono">Revenue</div>
        <div className="text-white/40 text-sm mt-2">{loading ? "Loading…" : "No data yet."}</div>
      </div>
    );
  }

  const profitAccent = rev.profit >= 0 ? "var(--bull)" : "var(--bear)";

  return (
    <div className="space-y-4" data-testid="revenue-panel">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-mono">Subscriptions · MRR</div>
          <h2 className="text-2xl sm:text-3xl text-white font-semibold tracking-tight mt-1">
            Revenue &amp; profit
          </h2>
        </div>
        {savedToast && (
          <div className="text-[11px] text-[var(--bull)] font-mono" data-testid="expense-saved-toast">
            ✓ saved
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <RevenueStat
          icon={DollarSign}
          label="Premium revenue"
          value={`${money(rev.pro_revenue)}/mo`}
          sub={`${rev.users.premium} × ${money(rev.prices.premium)}`}
          accent="#7aa2ff"
          testid="rev-pro"
        />
        <RevenueStat
          icon={DollarSign}
          label="Executive revenue"
          value={`${money(rev.elite_revenue)}/mo`}
          sub={`${rev.users.executive} × ${money(rev.prices.executive)}`}
          accent="#ffd166"
          testid="rev-elite"
        />
        <RevenueStat
          icon={TrendingUp}
          label="Total revenue"
          value={`${money(rev.total_revenue)}/mo`}
          sub="MRR"
          testid="rev-total"
        />
        <RevenueStat
          icon={Wallet}
          label="Total expenses"
          value={`${money(rev.total_expenses)}/mo`}
          sub="editable below"
          accent="#ff7676"
          testid="rev-expenses"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <RevenueStat
          icon={rev.profit >= 0 ? TrendingUp : TrendingDown}
          label="Estimated profit"
          value={`${money(rev.profit)}/mo`}
          sub="Revenue − Expenses"
          accent={profitAccent}
          testid="rev-profit"
        />
        <RevenueStat
          icon={BarChart3}
          label="Profit margin"
          value={`${rev.margin}%`}
          sub={rev.profit >= 0 ? "healthy if > 30%" : "expenses exceed revenue"}
          accent={profitAccent}
          testid="rev-margin"
        />
        <RevenueStat
          icon={DollarSign}
          label="Paid subscribers"
          value={`${rev.users.pro + rev.users.elite}`}
          sub={`pro ${rev.users.pro} · elite ${rev.users.elite} · free ${rev.users.free}`}
          accent="#7aa2ff"
          testid="rev-paid-count"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-3 sm:gap-4">
        <ExpenseEditor expenses={rev.expenses} onSave={saveExpenses} />
        <ProfitChart data={history} />
      </div>
    </div>
  );
}
